/**
 * کاتالوگِ مشترکِ رنگ — یک لیست برای همهٔ دراپ‌داون‌های رنگ (تولید و زنجیره تأمین).
 *
 * چرا: کدِ رنگ‌ها زیاد است و کاربر باید بفهمد رنگی که تایپ می‌کند قبلاً ثبت شده یا نه.
 * قبلاً هر صفحه لیستِ خودش را از دادهٔ همان صفحه می‌ساخت، پس رنگِ تازه فقط در همان
 * ردیف زنده بود و در لیستِ بعدی دوباره باید تایپ می‌شد.
 *
 * حالا: منبعِ حقیقت `production_options` با `kind="color"` است (مدلِ موجود، بدونِ
 * جدولِ جدید). به‌محضِ تأییدِ رنگِ تازه (Enter در Select) همان‌جا POST می‌شود و
 * بلافاصله در حافظهٔ محلی هم می‌نشیند تا بدونِ رفرش در دراپ‌داونِ بعدی دیده شود.
 *
 * اگر ثبتِ سراسری ناموفق بود (مثلاً کاربر مجوزِ ثبت ندارد) رنگ همچنان محلی
 * قابلِ استفاده است — ورودِ دادهٔ کاربر هیچ‌وقت به‌خاطرِ کاتالوگ متوقف نمی‌شود.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createProductionOption, getProductionOptions } from "../api/client";
import { COMMON_COLORS } from "../pages/supplychain/supplyCategories";

const norm = (v) => String(v ?? "").trim();

/**
 * @param {string[]} extra رنگ‌هایی که در دادهٔ همین صفحه دیده می‌شوند (دادهٔ قدیمی که
 *   هنوز در کاتالوگ نیست) تا Select هیچ‌وقت مقدارِ فعلی را گم نکند.
 */
export default function useColorOptions(extra = []) {
  const [catalog, setCatalog] = useState([]);   // از سرور
  const [local, setLocal] = useState([]);       // تازه‌ساخته‌ها در همین نشست

  useEffect(() => {
    let alive = true;
    getProductionOptions({ kind: "color", active_only: true })
      .then((r) => { if (alive) setCatalog((r.data || []).map((o) => o.value)); })
      .catch(() => {});   // نبودِ کاتالوگ نباید صفحه را بشکند
    return () => { alive = false; };
  }, []);

  // با رشتهٔ کلید کار می‌کنیم تا آرایهٔ تازه‌ساختهٔ صداکننده هر رندر memo را باطل نکند
  const extraKey = (extra || []).filter(Boolean).join("|");
  const options = useMemo(() => {
    const seen = new Map();
    const push = (v) => {
      const val = norm(v);
      if (val && !seen.has(val)) seen.set(val, { value: val, label: val });
    };
    COMMON_COLORS.forEach((c) => push(c.value));
    catalog.forEach(push);
    local.forEach(push);
    extraKey.split("|").forEach(push);
    return [...seen.values()];
  }, [catalog, local, extraKey]);

  /**
   * رنگِ تازه را همین لحظه در کاتالوگِ سراسری ثبت می‌کند.
   * تکراری بودن (۴۰۹) خطا نیست — یعنی از قبل هست، دقیقاً همان چیزی که می‌خواهیم.
   */
  const registerColor = useCallback(async (value) => {
    const val = norm(value);
    if (!val) return;
    setLocal((prev) => (prev.includes(val) ? prev : [...prev, val]));  // آنی، بدونِ انتظارِ شبکه
    try {
      await createProductionOption({ kind: "color", value: val });
    } catch (e) {
      if (e?.response?.status !== 409) {
        // فقط لاگ — کاربر نباید وسطِ ثبتِ کارکرد با خطای کاتالوگ متوقف شود
        console.warn("ثبتِ سراسریِ رنگ ناموفق بود:", val, e?.response?.status);
      }
    }
  }, []);

  return { colorOptions: options, registerColor };
}
